import React, { useEffect, useState } from "react";
import { message } from "antd";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Fade,
  Slide,
} from "@mui/material";
import imageCompression from "browser-image-compression";

const API_BASE_URL = "https://uktomars.co.uk/api";

const UpdateBrand = () => {
  const { id } = useParams();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchBrand = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/admin/brands`);
        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }
        const responseData = await res.json();
        const brands = Array.isArray(responseData?.data)
          ? responseData.data
          : Array.isArray(responseData)
            ? responseData
            : [];
        const brand = brands.find((item) => String(item.id) === String(id));

        if (!brand) {
          message.error("Brand not found");
          return;
        }

        setName(brand.name || "");
        setSlug(brand.slug || "");
        setImageUrl(brand.image_url || "");
        setPreviewUrl(brand.image_url || "");
      } catch (error) {
        console.error("Error loading brand:", error);
        message.error("Failed to load brand");
      } finally {
        setLoading(false);
      }
    };

    fetchBrand();
  }, [id]);

  const handleImageUpload = async (file) => {
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);
    setUploading(true);

    try {
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 512,
        useWebWorker: true,
        initialQuality: 0.85,
        alwaysKeepResolution: true,
      });

      const uploadFormData = new FormData();
      uploadFormData.append("image", compressedFile);

      const uploadRes = await fetch(`${API_BASE_URL}/admin/images/upload`, {
        method: "POST",
        body: uploadFormData,
      });

      if (!uploadRes.ok) {
        throw new Error(`Upload failed. Status: ${uploadRes.status}`);
      }

      const uploadData = await uploadRes.json();
      const uploadedPath =
        uploadData?.path || uploadData?.data?.path || uploadData?.url || "";

      if (!uploadedPath) {
        throw new Error("Upload response missing file path");
      }

      setImageUrl(uploadedPath);
      message.success("Image uploaded");
    } catch (error) {
      console.error("Image compression/upload failed:", error);
      message.error("Failed to upload image");
    } finally {
      URL.revokeObjectURL(localPreview);
      setUploading(false);
    }
  };

  const onChangeUpload = (e) => {
    if (e?.target?.files?.[0]) {
      handleImageUpload(e.target.files[0]);
    }
  };

  const handleReset = () => {
    setName("");
    setSlug("");
    setImageUrl("");
    setPreviewUrl("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      message.warning("Please enter brand name");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        slug: slug.trim(),
        image: imageUrl || null,
      };

      const res = await fetch(`${API_BASE_URL}/admin/brands/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }

      message.success("Brand updated successfully");
      window.location.href = "/admin/brands";
    } catch (error) {
      console.error("Error updating brand:", error);
      message.error("Failed to update brand");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <Typography>Loading brand data...</Typography>
      </Box>
    );
  }

  return (
    <Fade in timeout={500}>
      <Box sx={{ maxWidth: 1000, mx: "auto", my: 5, px: 2 }}>
        <Slide direction="up" in timeout={400}>
          <Paper elevation={6} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
            <Box component="form" onSubmit={onSubmit}>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Update Brand
                </Typography>
              </Box>

              <Box
                sx={{
                  mb: 4,
                  p: 2,
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  textAlign: "center",
                  backgroundColor: "grey.50",
                }}
              >
                <Box
                  sx={{
                    width: 180,
                    height: 180,
                    mx: "auto",
                    mb: 2,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "common.white",
                  }}
                >
                  {previewUrl || imageUrl ? (
                    <Box
                      component="img"
                      src={previewUrl || imageUrl}
                      alt="Brand preview"
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No image
                    </Typography>
                  )}
                </Box>
                <input
                  id="brand-logo-upload-update"
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={onChangeUpload}
                />
                <label htmlFor="brand-logo-upload-update">
                  <Button variant="outlined" component="span">
                    {uploading ? "Uploading..." : "Upload Brand Image"}
                  </Button>
                </label>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Brand Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", gap: 2, justifyContent: "center" }}
                  >
                    <Button variant="outlined" onClick={handleReset}>
                      Reset
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => (window.location.href = "/admin/brands")}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Submit"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Slide>
      </Box>
    </Fade>
  );
};

export default UpdateBrand;
